import { Graphics } from 'pixi.js';
import type { ArchetypeId, EnemyArchId , GearSlot, HeroId } from '@holdline/sim';
import { COLORS } from './layout';

/**
 * Vector placeholders in a chunky cartoon style: thick ink outlines, simple shapes.
 * Everything here is replaced by real sprites later; the shapes only need to read at a glance.
 */

const INK = COLORS.ink;

/** Icon of an archetype, centred at (cx, cy), roughly s*2 wide. */
export function drawGlyph(g: Graphics, arch: ArchetypeId, cx: number, cy: number, s: number): void {
  const ink = { color: INK, alpha: 0.9 };
  switch (arch) {
    case 'rapid':
      for (let k = 0; k < 3; k++) g.roundRect(cx - s, cy - s * 0.9 + k * s * 0.8, s * 2, s * 0.36, 2).fill(ink);
      break;
    case 'splash':
      g.circle(cx, cy, s * 0.85).stroke({ width: Math.max(2, s * 0.28), color: INK, alpha: 0.9 });
      g.circle(cx, cy, s * 0.3).fill(ink);
      break;
    case 'burn':
      g.poly([cx, cy - s, cx + s * 0.85, cy + s * 0.75, cx - s * 0.85, cy + s * 0.75]).fill(ink);
      g.poly([cx, cy - s * 0.2, cx + s * 0.35, cy + s * 0.5, cx - s * 0.35, cy + s * 0.5]).fill({ color: 0xffffff, alpha: 0.6 });
      break;
    case 'chain':
      g.moveTo(cx - s, cy - s * 0.9)
        .lineTo(cx - s * 0.15, cy - s * 0.15)
        .lineTo(cx + s * 0.35, cy - s * 0.4)
        .lineTo(cx + s, cy + s * 0.9)
        .stroke({ width: Math.max(2, s * 0.3), color: INK, alpha: 0.9, cap: 'round', join: 'round' });
      break;
    case 'repair':
      g.roundRect(cx - s * 0.25, cy - s, s * 0.5, s * 2, 2).fill(ink);
      g.roundRect(cx - s, cy - s * 0.25, s * 2, s * 0.5, 2).fill(ink);
      break;
    case 'barricade':
      // Stacked sandbags.
      for (let k = 0; k < 3; k++) {
        const w = s * (2 - k * 0.5);
        g.roundRect(cx - w / 2, cy + s * 0.55 - k * s * 0.6, w, s * 0.5, s * 0.25).fill(ink);
      }
      break;
    case 'barracks':
      // House with a door.
      g.poly([cx - s, cy - s * 0.1, cx, cy - s, cx + s, cy - s * 0.1]).fill(ink);
      g.rect(cx - s * 0.8, cy - s * 0.1, s * 1.6, s * 1.0).fill(ink);
      g.rect(cx - s * 0.2, cy + s * 0.3, s * 0.4, s * 0.6).fill({ color: 0xffffff, alpha: 0.7 });
      break;
    case 'archery':
      // Target.
      g.circle(cx, cy, s * 0.95).fill(ink);
      g.circle(cx, cy, s * 0.6).fill({ color: 0xffffff, alpha: 0.85 });
      g.circle(cx, cy, s * 0.28).fill(ink);
      break;
    case 'stable':
      // Wheel.
      g.circle(cx, cy, s * 0.9).stroke({ width: Math.max(2, s * 0.32), color: INK, alpha: 0.9 });
      g.moveTo(cx - s * 0.9, cy).lineTo(cx + s * 0.9, cy).stroke({ width: Math.max(2, s * 0.22), color: INK });
      g.moveTo(cx, cy - s * 0.9).lineTo(cx, cy + s * 0.9).stroke({ width: Math.max(2, s * 0.22), color: INK });
      break;
    case 'mine':
      // Crate with a coin.
      g.roundRect(cx - s * 0.9, cy - s * 0.7, s * 1.8, s * 1.5, 2).fill(ink);
      g.circle(cx, cy + s * 0.05, s * 0.45).fill({ color: 0xffffff, alpha: 0.85 });
      break;
  }
}

/** A building tile body: coloured block with ink outline and a roof for spawners. */
export function drawBuilding(g: Graphics, arch: ArchetypeId, color: number, w: number, h: number): void {
  const pad = 4;
  const spawner = arch === 'barracks' || arch === 'archery' || arch === 'stable';
  if (spawner) {
    // Roof
    g.poly([pad, h * 0.42, w / 2, pad + 2, w - pad, h * 0.42]).fill(color).stroke({ width: 3, color: INK, join: 'round' });
    g.roundRect(pad + 3, h * 0.4, w - pad * 2 - 6, h * 0.52, 5).fill(color).stroke({ width: 3, color: INK });
    g.rect(pad + 5, h * 0.42, w - pad * 2 - 10, h * 0.12).fill({ color: 0x000000, alpha: 0.15 });
  } else if (arch === 'barricade') {
    g.roundRect(pad, h * 0.2, w - pad * 2, h * 0.72, 8).fill(color).stroke({ width: 3, color: INK });
    for (let k = 0; k < 2; k++) {
      g.roundRect(pad + 4, h * 0.28 + k * h * 0.28, w - pad * 2 - 8, h * 0.18, 6).fill({ color: 0x000000, alpha: 0.18 });
    }
  } else {
    g.roundRect(pad, pad, w - pad * 2, h - pad * 2, 9).fill(color).stroke({ width: 3, color: INK });
    g.roundRect(pad + 3, pad + 3, w - pad * 2 - 6, (h - pad * 2) * 0.42, 6).fill({ color: 0xffffff, alpha: 0.12 });
  }
}

/** Blend two 0xRRGGBB colours; k = 0 gives a, k = 1 gives b. */
export function mixColor(a: number, b: number, k: number): number {
  const ch = (shift: number): number => {
    const x = (a >> shift) & 255;
    const y = (b >> shift) & 255;
    return Math.round(x + (y - x) * k) & 255;
  };
  return (ch(16) << 16) | (ch(8) << 8) | ch(0);
}

/** Deterministic 0..1 noise from a cell index and a salt, so tufts and pebbles never jitter between renders. */
function hash01(i: number, salt: number): number {
  let h = (i * 374761393 + salt * 668265263) | 0;
  h = Math.imul(h ^ (h >>> 13), 1274126177);
  return ((h ^ (h >>> 16)) >>> 0) / 4294967296;
}

/**
 * Flat cel-shaded grass: a soft base tone, a lighter facet along the top edge, a darker sliver at the
 * bottom, two or three tufts placed by the cell hash. No outline, the tiles read as one lawn.
 * `base` already carries the near/far gradient; `dim` darkens the enemy rows.
 */
export function drawGrassTile(g: Graphics, w: number, h: number, base: number, index: number, dim: boolean): void {
  const light = mixColor(base, 0xffffff, 0.14);
  const dark = mixColor(base, 0x1b2a1a, 0.18);
  g.roundRect(0, 0, w, h, 5).fill(base);
  g.roundRect(1, 1, w - 2, h * 0.28, 4).fill({ color: light, alpha: 0.55 });
  g.roundRect(1, h - 5, w - 2, 4, 2).fill({ color: dark, alpha: 0.45 });
  const tufts = 2 + Math.floor(hash01(index, 1) * 2);
  for (let k = 0; k < tufts; k++) {
    const tx = 6 + hash01(index, 10 + k) * (w - 12);
    const ty = 8 + hash01(index, 20 + k) * (h - 14);
    g.moveTo(tx - 3, ty + 3).lineTo(tx - 1, ty - 2).lineTo(tx + 1, ty + 3).lineTo(tx + 2, ty - 1).lineTo(tx + 4, ty + 3)
      .stroke({ width: 1.6, color: dark, alpha: 0.55, cap: 'round', join: 'round' });
  }
  if (dim) g.roundRect(0, 0, w, h, 5).fill({ color: 0x1b2a1a, alpha: 0.22 });
}

/**
 * The enemy road: sandy fill with a lighter top facet and a darker bottom rim, a few pebbles and one
 * faint chevron so the direction still reads. Softer than the old double chevrons.
 */
export function drawDirtTile(g: Graphics, w: number, h: number, base: number, index: number, dim: boolean): void {
  const light = mixColor(base, 0xffffff, 0.16);
  const dark = mixColor(base, 0x5a3f22, 0.3);
  g.roundRect(0, 0, w, h, 5).fill(base);
  g.roundRect(1, 1, w - 2, h * 0.26, 4).fill({ color: light, alpha: 0.5 });
  g.roundRect(1, h - 5, w - 2, 4, 2).fill({ color: dark, alpha: 0.35 });
  for (let k = 0; k < 3; k++) {
    const px = 8 + hash01(index, 30 + k) * (w - 16);
    const py = 8 + hash01(index, 40 + k) * (h - 16);
    g.ellipse(px, py, 2.2, 1.5).fill({ color: dark, alpha: 0.35 });
  }
  const cy = h * 0.46;
  g.moveTo(w * 0.38, cy).lineTo(w * 0.5, cy + 6).lineTo(w * 0.62, cy).stroke({ width: 2, color: dark, alpha: 0.45, cap: 'round', join: 'round' });
  if (dim) g.roundRect(0, 0, w, h, 5).fill({ color: 0x1b2a1a, alpha: 0.2 });
}

/** The base building: a squat concrete bunker with a door, slit windows and a flag. */
export function drawBunker(g: Graphics, w: number, h: number): void {
  const pad = 3;
  g.roundRect(pad, h * 0.22, w - pad * 2, h * 0.72, 10).fill(0x8d949c).stroke({ width: 3, color: INK });
  g.roundRect(pad + 6, h * 0.08, w - pad * 2 - 12, h * 0.3, 10).fill(0x6f767e).stroke({ width: 3, color: INK });
  const slits = Math.max(2, Math.floor(w / 26));
  for (let i = 0; i < slits; i++) {
    const x = pad + 12 + (i * (w - pad * 2 - 24)) / Math.max(1, slits - 1);
    g.roundRect(x - 5, h * 0.36, 10, 6, 2).fill(INK);
  }
  g.roundRect(w / 2 - 9, h * 0.56, 18, h * 0.36, 4).fill(0x3b3f47).stroke({ width: 2, color: INK });
  g.circle(w / 2 + 4, h * 0.74, 1.6).fill(0xf5c451);
  g.moveTo(w - pad - 14, h * 0.1).lineTo(w - pad - 14, h * 0.32).stroke({ width: 2, color: INK });
  g.poly([w - pad - 14, h * 0.1, w - pad - 2, h * 0.15, w - pad - 14, h * 0.21]).fill(0xe74c3c).stroke({ width: 1.5, color: INK });
}

export function drawRock(g: Graphics, w: number, h: number): void {
  const cx = w / 2;
  const cy = h * 0.52;
  g.ellipse(cx, cy, w * 0.36, h * 0.3).fill(COLORS.rock).stroke({ width: 3, color: COLORS.rockDark });
  g.ellipse(cx - w * 0.1, cy - h * 0.1, w * 0.13, h * 0.08).fill({ color: COLORS.rockLight, alpha: 0.7 });
  g.ellipse(cx + w * 0.2, cy + h * 0.14, w * 0.1, h * 0.06).fill({ color: COLORS.rockDark, alpha: 0.35 });
}

/** `wing` is the flyer's flap phase, -1..1; other archetypes ignore it. */
export function drawEnemy(g: Graphics, arch: EnemyArchId, x: number, y: number, r: number, color: number, wing = 0): void {
  switch (arch) {
    case 'flyer': {
      // A bat-winged drone: two wings that flap, a small body, big eyes.
      const spread = r * 1.6;
      const lift = r * 0.7 * wing;
      g.poly([x - r * 0.4, y, x - spread, y - r * 0.6 - lift, x - spread * 0.7, y + r * 0.35 - lift * 0.4]).fill(color).stroke({ width: 2, color: INK, join: 'round' });
      g.poly([x + r * 0.4, y, x + spread, y - r * 0.6 - lift, x + spread * 0.7, y + r * 0.35 - lift * 0.4]).fill(color).stroke({ width: 2, color: INK, join: 'round' });
      g.ellipse(x, y, r * 0.7, r * 0.85).fill(color).stroke({ width: 2, color: INK });
      eyes(g, x, y, r * 0.9);
      break;
    }
    case 'armored': {
      // Rounded plates with a seam and rivets: reads as "hit harder".
      g.roundRect(x - r, y - r * 0.9, r * 2, r * 1.8, r * 0.5).fill(color).stroke({ width: 3, color: INK });
      g.moveTo(x - r * 0.8, y - r * 0.15).lineTo(x + r * 0.8, y - r * 0.15).stroke({ width: 1.5, color: INK, alpha: 0.7 });
      g.moveTo(x, y - r * 0.15).lineTo(x, y + r * 0.9).stroke({ width: 1.5, color: INK, alpha: 0.7 });
      for (const dx of [-0.65, 0.65]) g.circle(x + r * dx, y + r * 0.55, r * 0.12).fill(INK);
      eyes(g, x, y - r * 0.3, r * 0.9);
      break;
    }
    case 'walker':
      g.circle(x, y, r).fill(color).stroke({ width: 2, color: INK });
      eyes(g, x, y, r);
      break;
    case 'runner':
      g.poly([x, y - r * 1.3, x + r, y + r * 0.8, x - r, y + r * 0.8]).fill(color).stroke({ width: 2, color: INK, join: 'round' });
      eyes(g, x, y + r * 0.1, r * 0.8);
      break;
    case 'brute':
      g.roundRect(x - r, y - r, r * 2, r * 2, 4).fill(color).stroke({ width: 2.5, color: INK });
      eyes(g, x, y - r * 0.1, r);
      break;
    case 'boss': {
      const pts: number[] = [];
      for (let i = 0; i < 6; i++) {
        const a = (i / 6) * Math.PI * 2 - Math.PI / 2;
        pts.push(x + Math.cos(a) * r, y + Math.sin(a) * r);
      }
      g.poly(pts).fill(color).stroke({ width: 3, color: INK, join: 'round' });
      eyes(g, x, y - r * 0.15, r);
      break;
    }
  }
}

function eyes(g: Graphics, x: number, y: number, r: number): void {
  const dx = r * 0.35;
  const er = Math.max(1.5, r * 0.18);
  g.circle(x - dx, y - r * 0.15, er).fill(0xffffff);
  g.circle(x + dx, y - r * 0.15, er).fill(0xffffff);
  g.circle(x - dx, y - r * 0.15, er * 0.5).fill(INK);
  g.circle(x + dx, y - r * 0.15, er * 0.5).fill(INK);
}

/** The hero: bigger, golden, with a plume and a sword. */
/** Gear slot icons: rifle, vest, exoskeleton legs, dog tag. `s` is roughly half the icon height. */
export function drawGearGlyph(g: Graphics, slot: GearSlot, cx: number, cy: number, s: number, color: number): void {
  if (slot === 'weapon') {
    g.roundRect(cx - s * 1.3, cy - s * 0.3, s * 2.6, s * 0.55, s * 0.2).fill(color).stroke({ width: 1.5, color: INK });
    g.roundRect(cx - s * 1.3, cy + s * 0.1, s * 0.8, s * 0.7, s * 0.15).fill(color).stroke({ width: 1.5, color: INK });
    g.rect(cx + s * 0.1, cy + s * 0.2, s * 0.35, s * 0.7).fill(INK);
  } else if (slot === 'armor') {
    g.roundRect(cx - s, cy - s, s * 2, s * 2.1, s * 0.5).fill(color).stroke({ width: 1.5, color: INK });
    g.poly([cx - s * 0.45, cy - s, cx + s * 0.45, cy - s, cx, cy - s * 0.35]).fill(INK);
    g.rect(cx - s * 0.5, cy + s * 0.2, s, s * 0.25).fill({ color: INK, alpha: 0.5 });
  } else if (slot === 'boots') {
    for (const dx of [-0.7, 0.7]) {
      g.roundRect(cx + dx * s - s * 0.3, cy - s, s * 0.6, s * 2.1, s * 0.2).fill(color).stroke({ width: 1.5, color: INK });
      g.circle(cx + dx * s, cy, s * 0.32).fill(INK);
    }
  } else {
    g.circle(cx, cy - s * 1.05, s * 0.45).stroke({ width: 1.5, color: INK });
    g.roundRect(cx - s * 0.7, cy - s * 0.65, s * 1.4, s * 1.8, s * 0.3).fill(color).stroke({ width: 1.5, color: INK });
    g.circle(cx, cy - s * 0.3, s * 0.18).fill(INK);
    g.rect(cx - s * 0.4, cy + s * 0.2, s * 0.8, s * 0.18).fill({ color: INK, alpha: 0.5 });
    g.rect(cx - s * 0.4, cy + s * 0.55, s * 0.6, s * 0.18).fill({ color: INK, alpha: 0.5 });
  }
}

export function drawHero(g: Graphics, x: number, y: number, color: number, hero: HeroId = 'commander'): void {
  const r = 9;
  g.circle(x, y, r).fill(color).stroke({ width: 2.5, color: INK });
  g.rect(x - r, y - r * 0.45, r * 2, r * 0.4).fill({ color: INK, alpha: 0.8 });
  if (hero === 'sniper') {
    // Hood, a scope glint and a long rifle held across the body.
    g.poly([x - r, y - r * 0.2, x, y - r - 5, x + r, y - r * 0.2]).fill(mixDark(color)).stroke({ width: 1.5, color: INK });
    g.moveTo(x - r - 6, y + r * 0.9).lineTo(x + r + 8, y - r * 0.8).stroke({ width: 3, color: 0x2d3436 });
    g.moveTo(x - r - 6, y + r * 0.9).lineTo(x + r + 8, y - r * 0.8).stroke({ width: 1, color: 0xdfe6e9, alpha: 0.6 });
    g.circle(x + r * 0.35, y - r * 0.15, 1.6).fill(0xff9f43);
    return;
  }
  if (hero === 'medic') {
    // Helmet cross and a satchel instead of the flag and rifle.
    g.rect(x - 1.2, y - r - 6, 2.4, 6).fill(0xffffff).stroke({ width: 1, color: INK });
    g.rect(x - 3.5, y - r - 4, 7, 2.4).fill(0xffffff).stroke({ width: 1, color: INK });
    g.roundRect(x + r - 3, y + 1, 7, 6, 1.5).fill(0xdfe6e9).stroke({ width: 1.5, color: INK });
    g.rect(x + r - 0.5, y + 2, 2, 4).fill(0xe74c3c);
    return;
  }
  g.poly([x - 2, y - r - 1, x + 2, y - r - 1, x, y - r - 7]).fill(0xe74c3c).stroke({ width: 1.5, color: INK });
  g.moveTo(x + r + 1, y + r * 0.6).lineTo(x + r + 1, y - r * 0.9).stroke({ width: 2.5, color: 0xdfe6e9 });
  g.rect(x + r - 2, y - r * 0.1, 6, 2).fill(INK);
}

function mixDark(color: number): number {
  const ch = (sh: number): number => Math.floor(((color >> sh) & 255) * 0.6) & 255;
  return (ch(16) << 16) | (ch(8) << 8) | ch(0);
}

export function drawSoldier(g: Graphics, kind: ArchetypeId, x: number, y: number, color: number): void {
  const r = 6;
  if (kind === 'stable') {
    g.roundRect(x - r * 1.3, y - r * 0.9, r * 2.6, r * 1.8, 3).fill(color).stroke({ width: 2, color: INK });
    g.circle(x - r * 0.7, y + r * 0.9, r * 0.45).fill(INK);
    g.circle(x + r * 0.7, y + r * 0.9, r * 0.45).fill(INK);
    return;
  }
  g.circle(x, y, r).fill(color).stroke({ width: 2, color: INK });
  // Helmet band
  g.rect(x - r, y - r * 0.45, r * 2, r * 0.35).fill({ color: INK, alpha: 0.8 });
  if (kind === 'archery') {
    g.moveTo(x + r * 1.1, y - r * 0.9).lineTo(x + r * 1.1, y + r * 0.9).stroke({ width: 1.5, color: INK });
  }
}
